"use client";

import { useEffect, useState } from "react";

type Language = "zh" | "en";
type SpeciesKey = "tbb" | "tc";

const content = {
  tbb: {
    code: "TBB",
    name: "Trypanosoma brucei brucei",
    zh: "布氏布氏锥虫",
    cells: "10,894",
    href: "/explore/?species=tbb",
    citation: "Briggs et al. 2021 author-hosted H5AD",
    stages: [
      ["Slender bloodstream", "细长血流型", "Mammal"],
      ["Intermediate", "分化过渡态", "Mammal"],
      ["Stumpy bloodstream", "粗短血流型", "Mammal"],
      ["Procyclic", "前循环型", "Tsetse"],
      ["Epimastigote", "上鞭毛体", "Tsetse"],
      ["Metacyclic", "后循环型", "Tsetse"],
    ],
    covered: [true, true, true, false, false, false],
  },
  tc: {
    code: "TC",
    name: "Trypanosoma cruzi",
    zh: "克氏锥虫",
    cells: "31,065",
    href: "/explore/?species=tc",
    citation: "Laidlaw et al. 2026 author-hosted H5AD",
    stages: [
      ["Epimastigote", "上鞭毛体", "Vector / culture"],
      ["Metacyclic", "后循环型", "Vector / culture"],
      ["Trypomastigote", "锥鞭毛体", "Mammal / culture"],
      ["Early amastigote", "早期无鞭毛体", "Mammal / culture"],
      ["Late amastigote", "晚期无鞭毛体", "Mammal / culture"],
      ["Transition", "阶段过渡态", "In vitro"],
    ],
    covered: [true, true, true, true, true, true],
  },
};

export function SpeciesCoverage() {
  const [language, setLanguage] = useState<Language>("zh");
  const [selected, setSelected] = useState<SpeciesKey>("tbb");
  const current = content[selected];

  useEffect(() => {
    const handleLanguage = (event: Event) =>
      setLanguage((event as CustomEvent<Language>).detail);
    window.addEventListener("trypcell-language", handleLanguage);
    setLanguage(document.body.dataset.language === "en" ? "en" : "zh");
    return () =>
      window.removeEventListener("trypcell-language", handleLanguage);
  }, []);

  return (
    <div className="coverage-explorer">
      <aside className="coverage-species">
        <h2>{language === "zh" ? "探索真实数据" : "Explore the data"}</h2>
        {(Object.keys(content) as SpeciesKey[]).map((key) => (
          <button
            type="button"
            key={key}
            className={key === selected ? "active" : ""}
            onClick={() => setSelected(key)}
          >
            <i>{content[key].name}</i>
            <span>{content[key].zh}</span>
            <strong>›</strong>
          </button>
        ))}
        {[
          "T. b. gambiense",
          "T. b. rhodesiense",
          "T. congolense",
          "T. vivax",
        ].map((name) => (
          <button
            type="button"
            className="disabled"
            disabled
            title={
              language === "zh"
                ? "尚未找到符合收录标准的公开寄生虫单细胞图谱"
                : "No qualifying public parasite single-cell atlas found yet"
            }
            key={name}
          >
            <i>{name}</i>
            <span>{language === "zh" ? "证据缺口" : "Evidence gap"}</span>
            <strong>—</strong>
          </button>
        ))}
        <small>
          {language === "zh"
            ? "未来物种一旦通过论文、仓库、许可和元数据核验，此处会直接解锁，不需要重做网站结构。"
            : "A future species unlocks here once its paper, repository, licence and metadata pass review; no redesign is required."}
        </small>
      </aside>

      <section className="coverage-map">
        <div className="coverage-map-heading">
          <div>
            <span>{current.code}</span>
            <h2>
              <i>{current.name}</i>
            </h2>
            <p>{current.zh}</p>
          </div>
          <div>
            <strong>{current.cells}</strong>
            <small>
              {language === "zh" ? "本站已载入细胞" : "cells loaded"}
            </small>
          </div>
        </div>
        <div className="stage-orbit">
          <div className="orbit-line" aria-hidden="true" />
          {current.stages.map(([en, zh, context], index) => (
            <div className="stage-node" key={en}>
              <span
                className={current.covered[index] ? "covered" : "not-covered"}
                aria-hidden="true"
              >
                {String(index + 1).padStart(2, "0")}
              </span>
              <strong>{language === "zh" ? zh : en}</strong>
              <small>{context}</small>
              <em>
                {current.covered[index]
                  ? language === "zh"
                    ? "本图谱有数据"
                    : "covered here"
                  : language === "zh"
                    ? "来自其他研究"
                    : "other studies"}
              </em>
            </div>
          ))}
        </div>
        <footer>
          <div>
            <span className="coverage-dot" />
            <p>
              {language === "zh"
                ? "这是数据覆盖图，不是按比例绘制的形态学图。颜色只表示当前载入图谱是否包含该作者阶段。"
                : "This is a data-coverage schematic, not a morphology drawing. Colour only indicates whether the loaded author atlas contains that stage."}
            </p>
          </div>
          <a href={current.href}>
            {language === "zh" ? "打开交互图谱" : "Open interactive atlas"} →
          </a>
        </footer>
        <p className="coverage-citation">{current.citation}</p>
      </section>
    </div>
  );
}
